/**
 * Pre-prompt priming for ONE's notification permission.
 *
 * Before this, the browser's native permission prompt fired cold: the first
 * post-login `getToken()` surfaced it with no explanation of what ONE would
 * send. A guardian who dismisses that prompt costs themselves every future
 * reunification push, and a `denied` decision is effectively permanent —
 * Chrome and Safari give no in-app way back, and repeat prompting is
 * increasingly penalised by the browser itself.
 *
 * So the native prompt is now gated behind an in-app explainer the user must
 * accept. The rules:
 *
 *  - `granted`  → nothing to do; registration proceeds as before.
 *  - `denied`   → nothing to do; registration already quiet-skips, and we
 *                 must not nag about a choice the browser will not re-ask.
 *  - `default`  → hold registration and show the explainer ONCE per cooldown.
 *
 * "Not now" is remembered for `PRIMING_COOLDOWN_MS` rather than forever: a
 * guardian who declines before their first reunification should be offered it
 * again next term, not silently never again. Storage failures fall back to
 * "show it" — being asked twice is a smaller harm than a guardian who never
 * learns push exists.
 */

/** How long a "Not now" suppresses the explainer. 30 days. */
export const PRIMING_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

export const PRIMING_DISMISSED_STORAGE_KEY = 'aegix.one.v1.pushPrimingDismissedAt';

/** `Notification.permission`, or `null` where the API does not exist. */
export function getNotificationPermission(): NotificationPermission | null {
  if (typeof Notification === 'undefined') return null;
  try {
    return Notification.permission;
  } catch {
    return null;
  }
}

function readDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(PRIMING_DISMISSED_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Storage blocked (private mode, site-data off) — treat as "never asked".
    return null;
  }
}

/** Remember a "Not now" so the explainer stays quiet for the cooldown. */
export function rememberPrimingDismissed(now: number = Date.now()): void {
  try {
    localStorage.setItem(PRIMING_DISMISSED_STORAGE_KEY, String(now));
  } catch {
    // Swallowed: a browser that cannot remember the dismissal will simply
    // offer the explainer again. Never block the UI on storage.
  }
}

/** Clear the cooldown (used by tests and by a successful grant). */
export function clearPrimingDismissed(): void {
  try {
    localStorage.removeItem(PRIMING_DISMISSED_STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * Should the in-app explainer be shown right now?
 *
 * Only in the `default` state, and only outside the cooldown. Any browser
 * without the Notification API is treated as "no push, no explainer".
 */
export function shouldShowPriming(now: number = Date.now()): boolean {
  if (getNotificationPermission() !== 'default') return false;
  const dismissedAt = readDismissedAt();
  if (dismissedAt === null) return true;
  return now - dismissedAt >= PRIMING_COOLDOWN_MS;
}

/**
 * May token registration run without surfacing a cold prompt?
 *
 * `true` for `granted` (register normally) and for `denied`/no-API (the
 * registration path quiet-skips on its own). `false` ONLY for `default`,
 * where the explainer owns the moment.
 */
export function canRegisterWithoutPriming(): boolean {
  return getNotificationPermission() !== 'default';
}
